import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ScrollText, Loader2, Save, History, Info } from "lucide-react";

function LegalTextCard({ item, onSaved }) {
  const [body, setBody] = useState(item.body || "");
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null);
  const [showHist, setShowHist] = useState(false);
  useEffect(() => { setBody(item.body || ""); }, [item.key]); // eslint-disable-line
  const dirty = body !== item.body;
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/settings/legal-texts/${item.key}`, { body });
      toast.success(`Testo salvato (v${data.version})`);
      onSaved(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio non riuscito"); }
    finally { setSaving(false); }
  };
  const loadHistory = async () => {
    try { const { data } = await api.get(`/settings/legal-texts/${item.key}/history`); setHistory(data); setShowHist(true); }
    catch (e) { toast.error("Storico non disponibile"); }
  };
  return (
    <Card className="rounded border-border p-5" data-testid={`legal-card-${item.key}`}>
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <div>
          <h2 className="font-heading font-semibold flex items-center gap-2"><ScrollText className="h-4 w-4 text-primary" strokeWidth={1.5} /> {item.title}</h2>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className="text-[11px] font-normal border-primary/40 text-primary" data-testid={`legal-version-${item.key}`}>Versione {item.version}{item.is_default ? " (predefinita)" : ""}</Badge>
            {item.updated_at && <span className="text-[11px] text-muted-foreground">Agg.: {new Date(item.updated_at).toLocaleString("it-IT")}</span>}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={loadHistory} data-testid={`legal-history-btn-${item.key}`}><History className="h-3.5 w-3.5 mr-1.5" /> Storico</Button>
      </div>
      <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} className="text-sm leading-relaxed font-mono" data-testid={`legal-textarea-${item.key}`} />
      <div className="flex items-center justify-between mt-3">
        <p className="text-[11px] text-muted-foreground">Ad ogni salvataggio la versione viene incrementata e la precedente archiviata nello storico. La versione usata viene registrata sui documenti generati (contratto).</p>
        <Button onClick={save} disabled={saving || !dirty} data-testid={`legal-save-${item.key}`}>{saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Salva</Button>
      </div>

      <Dialog open={showHist} onOpenChange={setShowHist}>
        <DialogContent className="max-w-2xl" data-testid={`legal-history-dialog-${item.key}`}>
          <DialogHeader><DialogTitle>Storico versioni — {item.title}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {(history || []).length === 0 && <p className="text-sm text-muted-foreground">Nessuna versione precedente archiviata.</p>}
            {(history || []).map((h, i) => (
              <div key={i} className="rounded border border-border p-3">
                <div className="flex items-center justify-between mb-1"><Badge variant="outline" className="text-[11px]">v{h.version}</Badge><span className="text-[11px] text-muted-foreground">{h.archived_at ? new Date(h.archived_at).toLocaleString("it-IT") : ""} · {h.archived_from_user || ""}</span></div>
                <p className="text-xs text-muted-foreground whitespace-pre-line">{h.body}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function LegalTexts() {
  const [items, setItems] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/settings/legal-texts"); setItems(data); }
    catch (e) { toast.error("Errore caricamento testi legali"); setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const onSaved = (updated) => setItems((prev) => prev.map((x) => (x.key === updated.key ? updated : x)));

  return (
    <div>
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center px-4 lg:px-8">
        <div>
          <h1 className="text-xl font-heading font-bold">Testi legali e disclaimer</h1>
          <p className="text-xs text-muted-foreground">Disclaimer e clausole inseriti automaticamente nei documenti cliente. Versionati e tracciati.</p>
        </div>
      </header>
      <div className="p-4 lg:p-8 space-y-6 max-w-4xl" data-testid="legal-texts-page">
        <div className="rounded border border-border bg-secondary/20 p-4 text-sm" data-testid="legal-distinzione-note">
          <div className="flex items-center gap-2 font-semibold mb-1"><Info className="h-4 w-4 text-primary" /> Subappalto ≠ Cessione del contratto</div>
          <p className="text-muted-foreground text-xs"><b>Subappalto</b>: FIER S.p.A. resta la controparte contrattuale del Cliente; un terzo esegue parte o tutte le lavorazioni. <b>Cessione del contratto</b>: un altro soggetto subentra nella posizione contrattuale; il Cliente deve essere informato e vanno rispettati consenso, forma e condizioni di legge. I due concetti non sono sinonimi.</p>
        </div>
        {!items ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          items.map((it) => <LegalTextCard key={it.key} item={it} onSaved={onSaved} />)
        )}
      </div>
    </div>
  );
}
